import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import type { ConversationTag, Uuid } from '@zenda/api-contract';
import { Icon } from './Icon';
import { useT } from '../lib/i18n';
import { useConversationTags, useTags } from '../lib/queries';

/**
 * Paleta fixa para tags novas, escolhida ciclicamente pela ordem de criacao.
 *
 * Pedir a cor no momento de criar a tag e uma pergunta a mais entre a atendente e a
 * coisa que ela queria fazer — e a resposta nao muda nada do trabalho. Cores
 * distinguiveis entre si importam; QUAL cor, nao.
 */
const PALETA = ['#3b82f6', '#ec4899', '#10b981', '#f59e0b', '#8b5cf6', '#f43f5e'] as const;

export function TagEditor({
  conversationId,
  tags,
  podeCriar: podeCriarNova = true,
}: {
  readonly conversationId: Uuid;
  readonly tags: readonly ConversationTag[];
  /** Fora da casa só se usa o catálogo de quem trouxe o médico: etiqueta é da operação dela. */
  readonly podeCriar?: boolean;
}): ReactElement {
  const t = useT();
  const catalogo = useTags(conversationId);
  const { add, remove } = useConversationTags(conversationId);
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const wrap = useRef<HTMLDivElement>(null);

  // Fecha ao clicar fora. `mousedown` e nao `click` para o menu sumir antes de o
  // clique chegar no que estava atras — senao a pessoa fecha o menu e seleciona uma
  // conversa sem querer.
  useEffect(() => {
    if (!aberto) return;
    function fora(e: MouseEvent): void {
      if (wrap.current !== null && !wrap.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const jaTem = new Set(tags.map((x) => x.id));
  const disponiveis = (catalogo.data ?? []).filter(
    (x) => !jaTem.has(x.id) && x.name.toLowerCase().includes(busca.trim().toLowerCase()),
  );
  const termo = busca.trim();
  const podeCriar =
    podeCriarNova &&
    termo !== '' &&
    !(catalogo.data ?? []).some((x) => x.name.toLowerCase() === termo.toLowerCase());

  function criar(): void {
    add.mutate(
      { name: termo, color: PALETA[(catalogo.data?.length ?? 0) % PALETA.length]! },
      { onSuccess: () => setBusca('') },
    );
  }

  return (
    <div className="thread__tags">
      {tags.map((tag) => (
        <span className="tagchip" key={tag.id} style={{ '--tag': tag.color } as CSSProperties}>
          {tag.name}
          <button
            type="button"
            className="tagchip__x"
            onClick={() => remove.mutate(tag.id)}
            aria-label={`${t.common.remove} ${tag.name}`}
          >
            <Icon name="x" size={11} />
          </button>
        </span>
      ))}

      <div className="tagmenu__wrap" ref={wrap}>
        <button type="button" className="tagadd" onClick={() => setAberto((v) => !v)}>
          <Icon name="plus" size={11} />
          {t.inbox.tagAdd}
        </button>

        {aberto ? (
          <div className="tagmenu">
            <input
              className="tagmenu__input"
              value={busca}
              maxLength={40}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && podeCriar) criar();
                if (e.key === 'Escape') setAberto(false);
              }}
              placeholder={podeCriarNova ? t.inbox.tagSearch : t.inbox.tagSearchOnly}
              autoFocus
            />

            {disponiveis.map((tag) => (
              <button
                key={tag.id}
                type="button"
                className="tagmenu__item"
                onClick={() => {
                  add.mutate({ tagId: tag.id });
                  setBusca('');
                }}
              >
                <span className="dlist__mark" style={{ background: tag.color }} />
                {tag.name}
                <span className="dlist__meta" style={{ marginLeft: 'auto' }}>
                  {tag.usageCount}
                </span>
              </button>
            ))}

            {podeCriar ? (
              <button type="button" className="tagmenu__item" onClick={criar}>
                <Icon name="plus" size={13} />
                {t.inbox.tagCreate(termo)}
              </button>
            ) : null}

            {disponiveis.length === 0 && !podeCriar ? (
              <p className="dsec__hint" style={{ padding: '6px 9px' }}>
                {!podeCriarNova && termo !== ''
                  ? t.inbox.tagSoDaDona
                  : (catalogo.data ?? []).length === 0
                    ? t.inbox.tagFirst
                    : t.inbox.tagAllUsed}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
